import { CurrencyTokenType, useCurrencyContext } from '@apeiron/currency'
import { ApeironPagingDataGridRef } from '@apeiron/temp'
import { Box, styled } from '@mui/material'
import VestingPNG from '@public/images/background-vesting.png'
import VestingSummaryClaimableSection from '@src/components/reward/vesting/VestingSummaryClaimableSection'
import VestingSummaryDataSection from '@src/components/reward/vesting/VestingSummaryDataSection'
import { useVesting } from '@src/hooks/contract/useVesting'
import { VestData } from '@src/types/vesting'
import * as R from 'ramda'
import { RefObject, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const ZERO_PLACEHOLDER = '--'

const Container = styled(Box)`
  background-color: #171717;
  background-image: url(${VestingPNG.src});
  background-position: right -50px bottom -35px;
  background-repeat: no-repeat;
  border-radius: 20px;
  background-size: 300px;
  border: 1px solid #5c5c5c;
  display: flex;
  padding: 30px 30px 60px 30px;
  width: 100%;
  ${props => props.theme.breakpoints.up('res1024')} {
    gap: 50px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    flex-direction: column;
    gap: 30px;
  }
`

const LeftContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  ${props => props.theme.breakpoints.up('res1024')} {
    border-right: 1px dotted #5c5c5c;
    flex: 0.55;
    gap: 40px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    border-bottom: 1px dotted #5c5c5c;
    padding-bottom: 20px;
    gap: 20px;
  }
`

const RightContainer = styled(Box)`
  ${props => props.theme.breakpoints.up('res1024')} {
    flex: 0.45;
  }
`

const DEFAULT_VEST_DATA: VestData = {
  currency: null,
  loading: false,
  tokenClaimable: '0',
  tokenClaimed: '0',
  tokenEligible: '0',
  tokenVested: '0'
}

const VestingSummaryCard = (props: Props) => {
  const { gridRef } = props

  const { t } = useTranslation()

  const [vestData, setVestData] = useState<VestData>(DEFAULT_VEST_DATA)

  const { getApeironPrice, getCurrency, getUSDPrice } = useCurrencyContext()

  const currency = getCurrency(CurrencyTokenType.Aprs) // ! this is hardcoded right now

  const { fetchClaimableAndClaimed, fetchVested } = useVesting()

  const handleOnFetchCardData = useCallback(async () => {
    if (R.isNotNil(currency)) {
      const [[tokenClaimable, tokenClaimed], tokenVested] = await Promise.all([
        fetchClaimableAndClaimed(),
        fetchVested()
      ])

      setVestData({
        currency,
        loading: false,
        tokenClaimable,
        tokenClaimed,
        tokenEligible: (
          BigInt(tokenClaimed) + BigInt(tokenClaimable)
        ).toString(),
        tokenVested
      })
    }
  }, [currency, fetchClaimableAndClaimed, fetchVested])

  const handleOnClaimed = useCallback(() => {
    if (gridRef?.current) {
      handleOnFetchCardData()
      gridRef.current.refreshData()
    }
  }, [gridRef, handleOnFetchCardData])

  const handleOnZero = useCallback((input: string) => {
    return input === '0' ? ZERO_PLACEHOLDER : input
  }, [])

  useEffect(() => {
    handleOnFetchCardData()
  }, [currency?.name])

  const unit = vestData.currency?.name || ''

  const tokenVested = useMemo(() => {
    const currencyType = vestData.currency?.type

    const token = handleOnZero(
      getApeironPrice(currencyType, vestData.tokenVested, {
        allowedLargeNumberAbbr: false
      })
    )

    const usd = handleOnZero(
      getUSDPrice(currencyType, vestData.tokenVested, true)
    )

    return {
      token,
      usd: `$${usd}`
    }
  }, [
    getApeironPrice,
    getUSDPrice,
    handleOnZero,
    vestData.currency?.type,
    vestData.tokenVested
  ])

  const tokenClaim = useMemo(() => {
    const currencyType = vestData.currency?.type

    const claimedToken = handleOnZero(
      getApeironPrice(currencyType, vestData.tokenClaimed, {
        allowedLargeNumberAbbr: false
      })
    )

    const eligibleToken = handleOnZero(
      getApeironPrice(currencyType, vestData.tokenEligible, {
        allowedLargeNumberAbbr: false
      })
    )

    const claimedUsd = handleOnZero(
      getUSDPrice(currencyType, vestData.tokenClaimed, true)
    )

    const eligibleUsd = handleOnZero(
      getUSDPrice(currencyType, vestData.tokenEligible, true)
    )

    return {
      token: `${claimedToken}/${eligibleToken}`,
      usd: `$${claimedUsd}/$${eligibleUsd}`
    }
  }, [
    getApeironPrice,
    getUSDPrice,
    handleOnZero,
    vestData.currency?.type,
    vestData.tokenClaimed,
    vestData.tokenEligible
  ])

  return (
    <Container>
      <LeftContainer>
        <VestingSummaryDataSection
          content={{
            remark: tokenVested.usd,
            subtext: unit,
            text: tokenVested.token
          }}
          title={t('vesting.vested')}
        />
        <VestingSummaryDataSection
          content={{
            remark: tokenClaim.usd,
            subtext: unit,
            text: tokenClaim.token
          }}
          title={t('vesting.claimed_and_eligible')}
        />
      </LeftContainer>
      <RightContainer>
        <VestingSummaryClaimableSection
          onClaimed={handleOnClaimed}
          vestData={vestData}
        />
      </RightContainer>
    </Container>
  )
}

type Props = {
  gridRef?: RefObject<ApeironPagingDataGridRef>
}

export default VestingSummaryCard
